import type { ActiveChatSession, InitParams, WidgetRuntimeState } from '@wix/wix-help-widget-common/types';

import type { DealerOfferDetails } from '../dealer';
import {
  subscribeToAppRender,
  subscribeToNavigation,
  subscribeToPostMessage,
  subscribeToWidgetExpansion,
  subscribeToWidgetStateChange,
} from '../events/subscribe';
import { WidgetSDKHandler } from '../events/WidgetSDKHandler';
import { renderWidget } from '../render';
import {
  chatbotSessionService,
  DealerOfferService,
  getWidgetSession,
  InitWidgetService,
  localizationService,
  updateWidgetSession,
} from '../services';
import { WidgetRuntimeService } from '../services/WidgetRuntimeService';
import { dispatch, getState } from '../store';
import type { UpdateWidgetSessionResponse } from '../types';
import type { IWidget } from '../widget';
import {
  publishContext,
  publishNewDealerOfferParams,
  publishOrigParams,
  publishStartUrl,
} from '../widget/publishers';
import { getStartUrl } from '../widget/startUrl';
import { widgetModel } from '../widgetModel';

// Responsible for the chatbot widget lifecycle when we are rendering only chatbot page experience.
export class ChatbotWidget implements IWidget {
  private initWidgetService: InitWidgetService;
  private widgetRuntimeService: WidgetRuntimeService;
  private dealerOfferService: DealerOfferService;
  private updateWidgetSessionPromise: Promise<UpdateWidgetSessionResponse>;

  constructor(initParams: InitParams) {
    if (!initParams || !initParams.origin) {
      throw new Error('Must pass an origin');
    }
    // setting the store with init params only once
    // we should find a better solution, if there is...
    dispatch('app/initParams', initParams);
    this.initWidgetService = new InitWidgetService();
    this.widgetRuntimeService = new WidgetRuntimeService();
    this.dealerOfferService = new DealerOfferService({ origin: initParams.origin });
  }

  async initialize(): Promise<void> {
    const [dealerOffer, initialWidgetData] = await Promise.all([
      this.dealerOfferService.fetch(),
      this.initWidgetService.fetch(),
      localizationService.init(widgetModel.getLocale()),
    ]);
    this.setWidgetIframeUrl(initialWidgetData?.activeChatSession, dealerOffer);
  }

  async refetchInitialData(): Promise<void> {
    await this.initWidgetService.fetch();
  }

  registerSubscribers(): void {
    subscribeToAppRender();
    subscribeToNavigation(this.handleNavigation);
    subscribeToPostMessage(new WidgetSDKHandler(this));
    subscribeToWidgetStateChange((data) => {
      this.updateWidgetSessionPromise = updateWidgetSession(data);
    });
    subscribeToWidgetExpansion();
  }

  render(): void {
    renderWidget();
  }

  getRuntimeState(): Promise<WidgetRuntimeState | undefined> {
    return this.widgetRuntimeService.get();
  }

  getDealerOfferDetails(): Promise<DealerOfferDetails | undefined> {
    return this.dealerOfferService.get();
  }

  getChatbotActiveSession(): ActiveChatSession | undefined {
    return getState().chatbot.chatbotActiveSession;
  }

  refreshRuntimeState(): void {
    this.widgetRuntimeService.refresh();
  }

  private handleNavigation = async (): Promise<void> => {
    dispatch('app/pageNavigate');

    const [widgetSession, activeChatSession, dealerOffer] = await Promise.all([
      this.updateWidgetSessionPromise ? this.updateWidgetSessionPromise : getWidgetSession(),
      chatbotSessionService.fetchActiveSession(),
      this.dealerOfferService.fetch(),
    ]);

    dispatch('chatbot/setSession', { activeChatSession, widgetSession });
    dispatch('widget/setSession', widgetSession);

    await Promise.all([
      publishStartUrl(),
      publishNewDealerOfferParams(),
      publishContext(),
      publishOrigParams(),
    ]);
    this.setWidgetIframeUrl(activeChatSession, dealerOffer);
    this.render();
  };

  private setWidgetIframeUrl(activeChatSession?: ActiveChatSession, dealerOffer?: DealerOfferDetails): void {
    const { initParams } = getState();
    const iframeUrl = getStartUrl(initParams, activeChatSession, dealerOffer);
    dispatch('app/setWidgetIframeSrc', iframeUrl);
  }
}
